import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { PriorityBadge, StatusBadge } from '../components/lifelink/PriorityBadge';
import { CATEGORY_CONFIG } from '../components/lifelink/CategoryGrid';
import ChatPanel from '../components/lifelink/ChatPanel';
import { FileText, MessageCircle, Clock, MapPin, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyRequestsPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const token = localStorage.getItem('lifelink_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMyRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/my-requests`, { headers });
      setRequests(data.requests || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || user === false) {
      navigate('/login');
      return;
    }
    fetchMyRequests();
  }, [user, navigate, fetchMyRequests]);

  return (
    <div className="min-h-screen bg-[#E8F4FD] pt-20 pb-8" data-testid="my-requests-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <FileText size={20} className="text-[#FF3B30]" />
          <h1 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#0D1B2A]">
            Le Mie Richieste
          </h1>
          <span className="text-xs font-mono text-[#6B8AB5] uppercase tracking-widest">{requests.length} totali</span>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#4A6B8A]">Caricamento...</div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 border border-[#B8D4E8] bg-white">
            <FileText size={40} className="mx-auto text-[#B8D4E8] mb-3" />
            <p className="text-sm text-[#4A6B8A]">Non hai ancora inviato richieste</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((req) => {
              const catConfig = CATEGORY_CONFIG.find(c => c.id === req.categoria);
              const Icon = catConfig?.icon;
              const color = catConfig?.color || '#4A6B8A';
              return (
                <div
                  key={req.id}
                  onClick={() => setSelectedRequest(req)}
                  className="border border-[#B8D4E8] bg-white p-4 hover:border-[#FF3B30]/40 transition-all cursor-pointer"
                  data-testid={`my-request-${req.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ color }}>
                      {Icon && <Icon size={22} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-bold text-[#0D1B2A] uppercase tracking-wide">
                          {t(req.categoria)}
                        </span>
                        <PriorityBadge priority={req.priorita} />
                        <StatusBadge status={req.stato} />
                        {req.chat_attiva && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-[#32D74B]/10 border border-[#32D74B]/30 text-[10px] font-mono text-[#32D74B] uppercase">
                            <MessageCircle size={10} />
                            Chat attiva
                          </span>
                        )}
                        {req.unread_messages > 0 && (
                          <span className="flex items-center justify-center w-5 h-5 bg-[#FF3B30] text-white text-[10px] font-bold rounded-full" data-testid={`unread-badge-${req.id}`}>
                            {req.unread_messages}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#4A6B8A] line-clamp-2 mb-1">{req.descrizione}</p>
                      <div className="flex items-center gap-4 text-[10px] text-[#6B8AB5] uppercase tracking-widest">
                        {req.indirizzo && (
                          <span className="flex items-center gap-1"><MapPin size={10} />{req.indirizzo}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock size={10} />{new Date(req.created_at).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Chat Modal */}
        {selectedRequest && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSelectedRequest(null)} data-testid="chat-modal">
            <div className="bg-white border border-[#B8D4E8] w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
              {/* Request info header */}
              <div className="p-4 border-b border-[#B8D4E8] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {(() => {
                    const cat = CATEGORY_CONFIG.find(c => c.id === selectedRequest.categoria);
                    const CatIcon = cat?.icon;
                    return CatIcon ? <CatIcon size={18} style={{ color: cat.color }} /> : null;
                  })()}
                  <div>
                    <h2 className="font-['Barlow_Condensed'] text-base font-bold uppercase tracking-tight text-[#0D1B2A]">
                      {t(selectedRequest.categoria)}
                    </h2>
                    <div className="flex gap-1 mt-0.5">
                      <PriorityBadge priority={selectedRequest.priorita} />
                      <StatusBadge status={selectedRequest.stato} />
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelectedRequest(null)} className="p-1 text-[#4A6B8A] hover:text-[#FF3B30]" data-testid="close-chat-btn">
                  <X size={18} />
                </button>
              </div>
              
              {/* Chat */}
              <div className="flex-1 overflow-hidden">
                <ChatPanel requestId={selectedRequest.id} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
